import { Alert, Box } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getContactCaptureRuntimeUrl } from '../../helpers';
import {
  ContactCapturePublicConfig,
  ContactCaptureSettings,
  ContactCaptureType,
} from '../../types';
import { PreviewState } from './ContactCapturePreviewToolbar';

type Props = {
  consentText: string;
  previewState: PreviewState;
  settings: ContactCaptureSettings;
  type: ContactCaptureType;
};

const CONFIG_DEBOUNCE_MS = 200;
const RUNTIME_TIMEOUT_MS = 8000;
const PREVIEW_ID = 'builder-preview';

type PreviewMessage =
  | { type: 'bhi-preview:ready' }
  | { type: 'bhi-preview:error'; message: string };

/** The preview bootstrap is compiled with the widget, rather than embedded as handwritten JS. */
const frameDocument = (runtimeUrl: string) => {
  const safeUrl = runtimeUrl.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;');
  return `<!doctype html><html><head><meta charset="utf-8" />
    <style>html, body { margin: 0; padding: 0; height: 100%; background: transparent; }</style>
    </head><body><script async src="${safeUrl}" data-contact-capture-builder-preview="true"></script></body></html>`;
};

export const ContactCapturePreviewCanvas = ({
  consentText,
  previewState,
  settings,
  type,
}: Props) => {
  const frameRef = useRef<HTMLIFrameElement>(null);
  const [runtimeError, setRuntimeError] = useState<string | null>(null);
  const [runtimeReady, setRuntimeReady] = useState(false);
  const runtimeUrl = useMemo(getContactCaptureRuntimeUrl, []);
  const srcDoc = useMemo(() => frameDocument(runtimeUrl), [runtimeUrl]);

  const config = useMemo<ContactCapturePublicConfig>(() => ({
    schemaVersion: 1,
    id: PREVIEW_ID,
    type,
    // The configured delay is honoured on the customer's site, not in the builder.
    settings: { ...settings, behavior: { ...settings.behavior, popupDelaySeconds: 0 } },
    consentText,
  }), [consentText, settings, type]);

  useEffect(() => {
    const onMessage = (event: MessageEvent<PreviewMessage>) => {
      if (event.source !== frameRef.current?.contentWindow) return;
      if (event.data?.type === 'bhi-preview:ready') {
        setRuntimeReady(true);
        setRuntimeError(null);
      }
      if (event.data?.type === 'bhi-preview:error') setRuntimeError(event.data.message);
    };
    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    if (runtimeReady) return undefined;
    const timeout = window.setTimeout(
      () => setRuntimeError(`Could not load ${runtimeUrl}`),
      RUNTIME_TIMEOUT_MS,
    );
    return () => window.clearTimeout(timeout);
  }, [runtimeReady, runtimeUrl]);

  // Keystrokes in the builder are coalesced, so the runtime re-mounts at most every 200ms.
  useEffect(() => {
    const timeout = window.setTimeout(() => {
      frameRef.current?.contentWindow?.postMessage(
        { type: 'bhi-preview:config', config, previewState },
        '*',
      );
    }, CONFIG_DEBOUNCE_MS);
    return () => window.clearTimeout(timeout);
  }, [config, previewState, runtimeReady]);

  if (runtimeError) {
    return (
      <Alert severity="warning" sx={{ m: 0 }}>
        {`${runtimeError}. The preview renders the published widget runtime, so run `}
        <code>npm start</code>
        {' so it is served.'}
      </Alert>
    );
  }

  return (
    <Box
      borderRadius={1}
      bgcolor="background.paper"
      sx={{
        flex: 1,
        minHeight: { xs: 520, lg: 0 },
        overflow: 'hidden',
        display: 'flex',
        justifyContent: 'center',
      }}
    >
      <Box
        component="iframe"
        ref={frameRef}
        title="Contact capture preview"
        srcDoc={srcDoc}
        // allow-scripts only: the frame gets an opaque origin and cannot reach the builder,
        // and the preview bootstrap renders locally without public API requests.
        sandbox="allow-scripts"
        border={0}
        sx={{
          width: '100%',
          height: '100%',
          display: 'block',
        }}
      />
    </Box>
  );
};
